import './TechInfoBulletins.scss';
import { ErrorDisplayComponent, ErrorBoundary, PageTitle, LoadingCircle, Breadcrumbs } from '@gplibraries/react/components';
import { ContentTable } from '@gplibraries/react/components';
import { useTokenProvider, useConfig, useLogger } from '@gplibraries/react/hooks';
import { useQuery } from '@tanstack/react-query';
import { fetchTechInfoBulletins } from '../../api/techDocuments.js';
import {useEffect, useState} from "react";

export default function TechInfoBulletins() {

  const logger = useLogger();
  const { dealerAccessApiScope, dealerAccessApiHost } = useConfig();
  const tableConfig = useConfig('techBulletinsTableConfig');
  const getToken = useTokenProvider({ scopes: [dealerAccessApiScope]});
  const [filteredBulletins, setFilteredBulletins] = useState<[]>([]);
  const [bulletinsFilters, setBulletinsFilters] = useState<{field: string, value: string}[]>([]);

  // middleware token query
  const { data: token, isLoading: tokenLoading } = useQuery({
    enabled: Boolean(getToken),
    queryKey: ['middlewareToken'],
    queryFn: () => getToken!()
  });

  // Fetch tech info bulletins
  const { isPending: loading, error, data: bulletins } = useQuery({
    enabled: Boolean(token),
    queryKey: ['tech_documents', 'bulletins'],
    queryFn: () => getToken!()
      .then(token => fetchTechInfoBulletins(dealerAccessApiHost, token!.accessToken))
      .then(res => res.json()),
    select: (data) => data.data
  })

  useEffect(() => {
    // Set initial filtered bulletins to all when data is fetched.
    if (bulletins && !filteredBulletins.length) {
      setFilteredBulletins(bulletins);
    }
    // Update filtered bulletins when filters change
    else if (filteredBulletins.length) {
      setFilteredBulletins(
        bulletins.filter((b: any) => bulletinsFilters.length
            // The bulletins must have the fields being filtered by AND the term IDs must match.
            ? bulletinsFilters.every(filter => (
                Object.keys(b).includes(filter.field) &&
                b[filter.field].tid == filter.value
            ))
            : true
        )
      )
    }
  }, [bulletins, bulletinsFilters]);

  const categorizationFields = [
    { field: 'field_dr_drills', label: 'DR - Drills' },
    { field: 'field_ge_general_information', label: 'GE - General Information' },
    { field: 'field_hi_hitches', label: 'HI - Hitches' },
    { field: 'field_me_metering_systems', label: 'ME - Metering Systems' },
    { field: 'field_mo_monitors', label: 'MO - Monitors' },
    { field: 'field_pa_precision_application', label: 'PA - Precision Application' },
    { field: 'field_pl_planters', label: 'PL - Planters' },
    { field: 'field_ru_row_units', label: 'RU - Row Units' },
    { field: 'field_tl_tillage', label: 'TL - Tillage' },
  ]

  function handleFilterChange(field: string, value: string) {
    // If value is an empty string, remove it.
    if (value === '') {
      setBulletinsFilters(bulletinsFilters.filter(f => f.field !== field))
    }
    // If filter field already exists, update the value. Otherwise add the filter
    else {
      setBulletinsFilters(bulletinsFilters.some((f: any) => f.field === field)
        ? [...bulletinsFilters.filter(f => f.field !== field), { field, value }]
        : [...bulletinsFilters, { field, value }]
      )
    }
  }

  // let error boundary handle this
  if (error) throw error;

  return (
    <>

      {/* Providing manual breadcrumbs here since "techincal-documents" is not an actual page.*/}
      <Breadcrumbs breadcrumbs={{
        'Technical Documents': '/technical-documents/bulletins',
        'Bulletins': '/technical-documents/bulletins'
      }}/>

      <PageTitle>Technical Information Documents</PageTitle>

      {loading && <LoadingCircle className={'large color'}/>}

      {bulletins?.length &&
        <section className={'block shadow full'}>
          <ErrorBoundary
            fallback={<ErrorDisplayComponent message={'Technical Information Documents could not be loaded.'}/>}
            logger={logger}
          >

            <div className="flex-row wrap table-filters">
              {categorizationFields
                //Only display filters if bulletins use the category
                .filter((category: any) => bulletins.some((b: any) => b?.[category.field]))
                .map((category: any) => (
                  <label key={category.field} htmlFor={category.field}>
                    {category.label}
                    <select name={category.field} defaultValue={''} onChange={(e) => handleFilterChange(category.field, e.target.value)}>
                      <option value="">-- Any --</option>
                      {
                        // get unique array of filter options and render an options element for them
                        bulletins
                          .filter((b: any) => b?.[category.field])
                          .map((b: any) => b[category.field])
                          .reduce((acc: any, current: any) => acc.some((cat: any) => cat.id === current.id)
                            ? acc
                            : [...acc, current]
                          , [])
                          .sort((a: any, b: any) => a.name.localeCompare(b.name))
                          .map((opt: any) => (
                            <option key={opt.id} value={opt.tid}>{opt.name}</option>
                          ))
                      }
                    </select>
                  </label>
                ))
              }
            </div>

            <ContentTable
              content={filteredBulletins
                .sort((a: any, b: any) => new Date(b.field_effective_date).getTime() - new Date(a.field_effective_date).getTime())
                .map((bulletin: any) => ({
                  bulletin_id: bulletin.title,
                  bulletin_file: { text: bulletin?.field_document_file?.filename, url: bulletin?.field_document_file?.uri },
                  bulletin_description: bulletin?.field_bulletin_description?.value,
                  effective_date: bulletin?.field_effective_date,
                  expiration_date: bulletin?.field_expiration_date,
                }))}
              tableConfig={tableConfig}
              paginated={true}
            />

          </ErrorBoundary>
        </section>
      }

    </>
  );
}